import { Station, Train, MetroLine, PathNode, Player, Point } from '../types';

// Calculate distance between two points
export function calculateDistance(a: Point, b: Point): number {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return Math.sqrt(dx * dx + dy * dy);
}

// Calculate distance between two stations
export function calculateStationDistance(
  stationA: Station,
  stationB: Station
): number {
  return calculateDistance(stationA.position, stationB.position);
}

// A* pathfinding algorithm for metro navigation
export function findPath(
  stations: Map<string, Station>,
  _lines: Map<string, MetroLine>,
  _trains: Map<string, Train>,
  startStationId: string,
  targetStationId: string,
  currentTime: number
): PathNode[] | null {
  // If start and target are the same, return an empty path
  if (startStationId === targetStationId) {
    return [{ stationId: startStationId, cost: 0 }];
  }

  const openSet: PathNode[] = [];
  const closedSet = new Set<string>();
  const gScore = new Map<string, number>();
  const fScore = new Map<string, number>();
  const cameFrom = new Map<string, {
    stationId: string;
    trainId?: string;
    arrivalTime: number;
  }>();

  // Get start and target stations
  const startStation = stations.get(startStationId);
  const targetStation = stations.get(targetStationId);
  
  if (!startStation || !targetStation) {
    return null;
  }

  // Initialize with the starting node
  gScore.set(startStationId, 0);
  fScore.set(
    startStationId,
    calculateStationDistance(startStation, targetStation)
  );
  openSet.push({
    stationId: startStationId,
    cost: fScore.get(startStationId) || 0,
    arrivalTime: currentTime,
  });

  // Main A* loop
  while (openSet.length > 0) {
    // Sort open set by cost (lowest first)
    openSet.sort((a, b) => a.cost - b.cost);
    
    // Get the node with the lowest fScore
    const current = openSet.shift();
    if (!current) break;
    
    // If we've reached the target
    if (current.stationId === targetStationId) {
      return reconstructPath(
        cameFrom,
        current.stationId,
        startStationId,
        stations
      );
    }

    // Add to closed set
    closedSet.add(current.stationId);
    
    // Get current station
    const currentStation = stations.get(current.stationId);
    if (!currentStation) continue;

    // Examine all connected stations (direct walking connections)
    currentStation.connections.forEach((neighborId) => {
      if (closedSet.has(neighborId)) return;
      
      const neighborStation = stations.get(neighborId);
      if (!neighborStation) return;
      
      // Calculate cost (walking time = distance / walking speed)
      const walkingSpeed = 100; // Pixels per second
      const distance = calculateStationDistance(currentStation, neighborStation);
      const walkingTime = distance / walkingSpeed;
      
      // Total cost to this neighbor via walking
      const tentativeGScore = (gScore.get(current.stationId) || 0) + walkingTime;
      
      // Check if this path is better than any previous ones
      if (
        !gScore.has(neighborId) ||
        tentativeGScore < (gScore.get(neighborId) || Infinity)
      ) {
        // This path is the best so far, record it
        cameFrom.set(neighborId, {
          stationId: current.stationId,
          arrivalTime: (current.arrivalTime || currentTime) + walkingTime * 1000,
        });
        
        gScore.set(neighborId, tentativeGScore);
        
        // Calculate f-score (g-score + heuristic)
        const heuristic = calculateStationDistance(neighborStation, targetStation) / walkingSpeed;
        fScore.set(neighborId, tentativeGScore + heuristic);
        
        // Add to open set if not already there
        if (!openSet.some((node) => node.stationId === neighborId)) {
          openSet.push({
            stationId: neighborId,
            cost: fScore.get(neighborId) || 0,
            arrivalTime: (current.arrivalTime || currentTime) + walkingTime * 1000,
          });
        }
      }
    });

    // Also check train connections - trains that stop at current station
    // This is more complex and would require checking timetables
    // TODO: Implement train connections in pathfinding
  }

  // No path found
  return null;
}

// Reconstruct the path from cameFrom mapping
function reconstructPath(
  cameFrom: Map<string, { stationId: string; trainId?: string; arrivalTime: number }>,
  current: string,
  start: string,
  stations: Map<string, Station>
): PathNode[] {
  const path: PathNode[] = [{ stationId: current, cost: 0 }];
  
  while (current !== start) {
    const prev = cameFrom.get(current);
    if (!prev) break;
    
    const prevStation = stations.get(prev.stationId);
    const currentStation = stations.get(current);
    
    if (prevStation && currentStation) {
      path.unshift({
        stationId: prev.stationId,
        cost: calculateStationDistance(prevStation, currentStation),
        nextStationId: current,
        trainId: prev.trainId,
        arrivalTime: prev.arrivalTime,
      });
    }
    
    current = prev.stationId;
  }
  
  return path;
}

// Utility function to find the best station to head to next
export function findNextBestStation(
  player: Player,
  target: Player,
  stations: Map<string, Station>,
  lines: Map<string, MetroLine>
): string | null {
  if (!player.currentStationId || !target.currentStationId) {
    return null;
  }
  
  // Get current and target stations
  const currentStation = stations.get(player.currentStationId);
  const targetStation = stations.get(target.currentStationId);
  
  if (!currentStation || !targetStation) {
    return null;
  }
  
  // Find path to target
  const path = findPath(
    stations,
    lines,
    new Map(), // Empty trains map for now
    player.currentStationId,
    target.currentStationId,
    Date.now()
  );
  
  if (!path || path.length < 2) {
    return null;
  }
  
  // Return the next station in the path
  return path[1].stationId;
}

// Function to determine which train to board
export function findBestTrainToBoard(
  player: Player,
  trains: Map<string, Train>,
  stations: Map<string, Station>,
  lines: Map<string, MetroLine>,
  targetStationId: string
): string | null {
  if (!player.currentStationId) {
    return null;
  }
  
  const currentStation = stations.get(player.currentStationId);
  if (!currentStation) {
    return null;
  }
  
  // Get all trains currently at this station
  const availableTrains: Train[] = [];
  
  trains.forEach(train => {
    const line = lines.get(train.lineId);
    if (!line) return;
    
    const currentStationId = line.stations[train.currentStationIndex];
    
    if (currentStationId === player.currentStationId && 
        Date.now() < train.actualDepartureTime) {
      availableTrains.push(train);
    }
  });
  
  if (availableTrains.length === 0) {
    return null;
  }
  
  // Find the train that's heading toward the target or a useful interchange
  let bestTrain: Train | null = null;
  let bestScore = Infinity;
  
  availableTrains.forEach(train => {
    const line = lines.get(train.lineId);
    if (!line) return;
    
    // Check if this line passes through the target
    if (line.stations.includes(targetStationId)) {
      // Get index of current and target stations
      const currentIndex = line.stations.indexOf(player.currentStationId!);
      const targetIndex = line.stations.indexOf(targetStationId);
      
      // Check if the train is heading in the right direction
      if ((targetIndex > currentIndex && train.direction === 1) || 
          (targetIndex < currentIndex && train.direction === -1)) {
        // This train goes directly to the target in the right direction!
        const score = Math.abs(targetIndex - currentIndex) * 0.5; // Lower is better
        if (score < bestScore) {
          bestTrain = train;
          bestScore = score;
        }
      }
    } else {
      // This line doesn't go to the target directly
      // Check if it passes through any interchange stations
      let bestInterchangeScore = Infinity;
      
      for (let i = 0; i < line.stations.length; i++) {
        const stationId = line.stations[i];
        const station = stations.get(stationId);
        
        if (station && station.isInterchange && stationId !== player.currentStationId) {
          // This is a potential interchange
          const currentIndex = line.stations.indexOf(player.currentStationId!);
          
          // Check if the train is heading toward the interchange
          if ((i > currentIndex && train.direction === 1) || 
              (i < currentIndex && train.direction === -1)) {
            
            // Calculate score based on distance to interchange
            const score = Math.abs(i - currentIndex) + 
                          calculateDistance(station.position, stations.get(targetStationId)!.position) * 0.001;
            
            if (score < bestInterchangeScore) {
              bestInterchangeScore = score;
            }
          }
        }
      }
      
      // If we found a good interchange, consider this train
      if (bestInterchangeScore < Infinity && bestInterchangeScore < bestScore) {
        bestTrain = train;
        bestScore = bestInterchangeScore;
      }
    }
  });
  
  // Use type assertion to ensure TypeScript knows bestTrain has an id property
  return bestTrain ? (bestTrain as Train).id : null;
} 